/**
 * useContactAnalytics.js
 *
 * Centralised analytics hook for both Contact Us forms:
 *   - ContactUsForm   (general enquiry → /contactUs)
 *   - ContactUSPrenatal (maternal yoga enquiry → /prenatalQuery)
 *
 * Mirrors the exact pattern of useBlogAnalytics.js / usePackagesAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [ContactAnalytics] prefix
 *   - react-ga4 used in production
 *
 * HOW TO USE:
 *   const analytics = useContactAnalytics();
 *   analytics.trackFormView("general");
 *   analytics.trackFieldFocus("email", "general");
 *   analytics.trackSubmitAttempt("general", hasErrors);
 *   analytics.trackSubmitSuccess("general", { jobTitle: "Student" });
 *   analytics.trackSubmitError("general", "network");
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [ContactAnalytics] in real time.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[ContactAnalytics] ${eventName}`, "color:#0077CC;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useContactAnalytics = () => {

  // ── Page / section view ───────────────────────────────────────────────────────

  /**
   * @param {"general" | "prenatal"} formType
   * @param {string} url
   */
  const trackFormView = useCallback((formType, url) => {
    send("contact_form_view", {
      form_type:     formType,
      page_title:    formType === "prenatal"
                       ? "Contact — Prenatal & Postnatal Yoga"
                       : "Contact — General Enquiry",
      page_location: url,
    });
  }, []);

  // ── Field interactions ────────────────────────────────────────────────────────

  /**
   * Call on onFocus for any field you want to track engagement on.
   * @param {string} fieldName  e.g. "email", "mobile"
   * @param {"general" | "prenatal"} formType
   */
  const trackFieldFocus = useCallback((fieldName, formType) => {
    send("contact_field_focus", {
      field_name: fieldName,
      form_type:  formType,
    });
  }, []);

  // ── Prenatal toggle ───────────────────────────────────────────────────────────

  /**
   * @param {"prenatal" | "postnatal"} selected
   */
  const trackPrenatalToggle = useCallback((selected) => {
    send("contact_prenatal_toggle", {
      selected,
      event_label: selected === "prenatal" ? "🤰 Prenatal" : "🍼 Postnatal",
    });
  }, []);

  // ── Form submission lifecycle ─────────────────────────────────────────────────

  /**
   * Fired every time the user clicks Submit (before validation result).
   * @param {"general" | "prenatal"} formType
   * @param {boolean} hasErrors  — true if client-side validation failed
   */
  const trackSubmitAttempt = useCallback((formType, hasErrors) => {
    send("contact_submit_attempt", {
      form_type:  formType,
      has_errors: hasErrors,
    });
  }, []);

  /**
   * Fired after a successful API response.
   * @param {"general" | "prenatal"} formType
   * @param {object} meta  — non-PII context, e.g. { jobTitle, isPrenatal }
   */
  const trackSubmitSuccess = useCallback((formType, meta = {}) => {
    send("contact_submit_success", {
      form_type: formType,
      ...meta,
    });
  }, []);

  /**
   * Fired if the API call throws.
   * @param {"general" | "prenatal"} formType
   * @param {string} reason  — e.g. "network", "server"
   */
  const trackSubmitError = useCallback((formType, reason = "unknown") => {
    send("contact_submit_error", {
      form_type:    formType,
      error_reason: reason,
    });
  }, []);

  // ── Phone CTA ─────────────────────────────────────────────────────────────────

  /**
   * @param {"general" | "prenatal"} formType
   * @param {"panel" | "footer_line"} location  — where on the page the link is
   */
  const trackPhoneClick = useCallback((formType, location = "footer_line") => {
    send("contact_phone_click", {
      form_type: formType,
      location,
      phone:     "+917838866331",
    });
  }, []);

  return {
    trackFormView,
    trackFieldFocus,
    trackPrenatalToggle,
    trackSubmitAttempt,
    trackSubmitSuccess,
    trackSubmitError,
    trackPhoneClick,
  };
};

export default useContactAnalytics;
